// src/modules/requerimiento/views/adicion/RevisionSistemas.jsx
// Revisión en Sistemas de una adición, con el mismo diseño que la app Android:
// resumen con contadores, equipos agrupados por vehículo como lista de verificación
// y, al final, la nota de adición y los datos del requerimiento plegados.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Card, CardActionArea, CardContent, Typography, Chip, Stack,
  CircularProgress, Alert, Button, IconButton, LinearProgress,
  Menu, MenuItem, ListItemIcon,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import PrintIcon from "@mui/icons-material/Print";
import MemoryIcon from "@mui/icons-material/Memory";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import { getRequerimiento, getNotaPdf, getAdicionAsignados } from "../../services/reqSolicitudes";
import ENV from "../../../../config/env";
import useVerificacionReporte, { contarReportes } from "../../hooks/useVerificacionReporte";
import { ItemVerificacion, LineaReporte, PlataformasDialog } from "../../components/ReporteComponents";
import CentroEquipoDetalleDialog from "../../components/CentroEquipoDetalleDialog";
import ResumenRevision from "../../components/ResumenRevision";
import DatosRequerimientoPlegables from "../../components/DatosRequerimientoPlegables";
import EquipoInstaladoDialog, {
  getChips, getChipNumber, Observacion,
} from "../../components/adicion/EquipoInstaladoDialog";

const AZUL_EQUIPOS = "#0284C7";
const SIN_VEHICULO = "Sin vehículo asignado";

const tienePlaca = (m) => !!String(m?.placa ?? "").trim();
const tieneFoto = (m) => !!String(m?.imagen_url ?? "").trim();
const tipoBandera = (m) => {
  const b = String(m?.bandera ?? "").trim().toLowerCase();
  if (b === "si" || b === "bandera") return "Bandera";
  if (b === "sensor") return "Sensor";
  return null;
};

export default function AdicionRevisionSistemas({ cod, onToast }) {
  const [req,          setReq]          = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [err,          setErr]          = useState(null);
  const [printing,     setPrinting]     = useState(false);
  const [asignados,    setAsignados]    = useState([]);
  const [loadingAsign, setLoadingAsign] = useState(false);
  const [errAsign,     setErrAsign]     = useState(null);
  const [detalle,      setDetalle]      = useState(null); // { row, editar }
  const [centroEquipo, setCentroEquipo] = useState(null);
  const [plataformasDe, setPlataformasDe] = useState(null); // equipo cuyo diálogo de plataformas está abierto
  const { reportes, verificarUno, verificarTodos } = useVerificacionReporte();

  const openCentroEquipo = useCallback((identificador, tipo = "modem") => {
    const value = identificador == null ? "" : String(identificador).trim();
    if (!value) return;
    setCentroEquipo({ tipo, identificador: value });
  }, []);

  const toAbsUrl = useCallback((u) => {
    if (!u) return "";
    if (/^https?:\/\//i.test(u)) return u;
    return ENV.API_BASE_URL ? `${ENV.API_BASE_URL}${u}` : u;
  }, []);

  const fetchAsignados = useCallback(async () => {
    if (!cod) return;
    setLoadingAsign(true);
    setErrAsign(null);
    try {
      const { data } = await getAdicionAsignados(cod);
      const lista = Array.isArray(data) ? data : data?.data || data?.items || [];
      setAsignados(lista);
      return lista;
    } catch (e) {
      setErrAsign(e?.response?.data?.message || e?.message || "Error al cargar asignados");
    } finally {
      setLoadingAsign(false);
    }
  }, [cod]);

  const fetchReq = useCallback(async () => {
    if (!cod) return;
    setLoading(true);
    setErr(null);
    try {
      const { data } = await getRequerimiento(cod);
      setReq(data);
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Error al cargar requerimiento");
    } finally {
      setLoading(false);
    }
  }, [cod]);

  useEffect(() => {
    fetchReq();
    fetchAsignados().then((lista) => { if (lista?.length) verificarTodos(lista); });
  }, [fetchReq, fetchAsignados, verificarTodos]);

  const handleImprimir = async () => {
    try {
      setPrinting(true);
      const { data } = await getNotaPdf(cod);
      const blob = new Blob([data], { type: "application/pdf" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.target = "_blank"; a.download = `nota-adicion-${cod}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      onToast?.({ sev: "error", msg: e?.response?.data?.error || e?.message || "No se pudo generar el PDF" });
    } finally {
      setPrinting(false);
    }
  };

  // Árbol por vehículo: la placa es el título y de ella cuelgan sus equipos.
  const grupos = useMemo(() => {
    const mapa = new Map();
    asignados.forEach((m) => {
      const placa = String(m?.placa ?? "").trim() || SIN_VEHICULO;
      if (!mapa.has(placa)) mapa.set(placa, []);
      mapa.get(placa).push(m);
    });
    return Array.from(mapa.entries());
  }, [asignados]);

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress size={28} /></Box>;
  }

  if (err) {
    return <Alert severity="error" action={<Button size="small" onClick={fetchReq}>Reintentar</Button>}>{err}</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Resumen
        asignados={asignados}
        cargando={loadingAsign}
        reportes={reportes}
        onVerificar={() => verificarTodos(asignados)}
      />

      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Vehículos</Typography>

      {loadingAsign ? (
        <LinearProgress />
      ) : errAsign ? (
        <Alert severity="error" action={<Button size="small" onClick={fetchAsignados}>Reintentar</Button>}>{errAsign}</Alert>
      ) : asignados.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No hay módems adicionados para este requerimiento.</Typography>
      ) : (
        grupos.map(([placa, modems]) => (
          <Box key={placa}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <DirectionsCarIcon fontSize="small" sx={{ color: placa === SIN_VEHICULO ? "error.main" : "text.secondary" }} />
              <Typography sx={{ fontWeight: 800, color: placa === SIN_VEHICULO ? "error.main" : "text.primary" }}>
                {placa}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                · {modems.length === 1 ? "1 equipo" : `${modems.length} equipos`}
              </Typography>
            </Stack>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
              {modems.map((m, idx) => (
                <ModemRevisionCard
                  key={m.num_sim || m.id_historial || idx}
                  modem={m}
                  onVer={() => setDetalle({ row: m, editar: false })}
                  onEditar={() => setDetalle({ row: m, editar: true })}
                  onVerEquipo={() => openCentroEquipo(m.num_sim || m.emai)}
                  reporte={reportes[m.num_sim || m.emai]}
                  onVerReporte={() => setPlataformasDe(m)}
                />
              ))}
            </Box>
          </Box>
        ))
      )}

      {/* Nota en PDF y datos del requerimiento: de consulta, al final y compactos. */}
      <LoadingButton
        loading={printing}
        loadingPosition="start"
        startIcon={<PrintIcon />}
        variant="outlined"
        onClick={handleImprimir}
        sx={{ textTransform: "none", py: 1 }}
      >
        {printing ? "Generando…" : "Ver nota de adición (PDF)"}
      </LoadingButton>

      {req && <DatosRequerimientoPlegables req={req} />}

      <EquipoInstaladoDialog
        row={detalle?.row ?? null}
        editarAlAbrir={!!detalle?.editar}
        cod={cod}
        onClose={() => setDetalle(null)}
        onSaved={async (actualizado) => {
          setDetalle((d) => (d ? { ...d, row: { ...d.row, ...actualizado } } : d));
          await fetchAsignados();
        }}
        onToast={onToast}
        onVerEquipo={(numSim) => openCentroEquipo(numSim)}
        toAbsUrl={toAbsUrl}
      />

      <PlataformasDialog
        modem={plataformasDe}
        reporte={plataformasDe ? reportes[plataformasDe.num_sim || plataformasDe.emai] : null}
        onReintentar={() => plataformasDe && verificarUno(plataformasDe)}
        onVerEquipo={() => plataformasDe && openCentroEquipo(plataformasDe.num_sim || plataformasDe.emai)}
        onClose={() => setPlataformasDe(null)}
      />

      <CentroEquipoDetalleDialog
        open={!!centroEquipo}
        tipo={centroEquipo?.tipo || "modem"}
        identificador={centroEquipo?.identificador}
        onClose={() => setCentroEquipo(null)}
      />
    </Stack>
  );
}

// ── Resumen ────────────────────────────────────────────────────

/** Cabecera de color: qué hay que hacer, el avance como contadores y un mensaje final. */
function Resumen({ asignados, cargando, reportes, onVerificar }) {
  const total = asignados.length;
  const conPlaca = asignados.filter(tienePlaca).length;
  const conFoto = asignados.filter(tieneFoto).length;
  const { verificando, reportando, desactualizados, sinReporte } = contarReportes(asignados, reportes);
  const todoBien = total > 0 && !verificando && conPlaca === total && reportando === total;

  const mensaje = cargando
    ? "Cargando equipos…"
    : total === 0
      ? "Todavía no hay equipos adicionados."
      : verificando
        ? "Verificando si los equipos reportan…"
        : todoBien
          ? conFoto < total
            ? `Todo en orden. ${total - conFoto} equipo(s) sin foto de la instalación.`
            : "Todo en orden. Ya se puede finalizar."
          : conPlaca < total
            ? `${total - conPlaca} equipo(s) sin vehículo asignado.`
            : sinReporte > 0
              ? `${sinReporte} equipo(s) no reportan todavía.`
              : desactualizados > 0
                ? `${desactualizados} equipo(s) no mandan datos hace días.`
                : "Revisa los equipos marcados abajo.";

  return (
    <ResumenRevision
      descripcion="Revisa que cada equipo quede instalado en su vehículo y reportando en la plataforma."
      contadores={total > 0 && !cargando ? [
        { etiqueta: "En vehículo", valor: conPlaca, total },
        { etiqueta: "Reportando", valor: verificando ? null : reportando, total },
        { etiqueta: "Con foto", valor: conFoto, total },
      ] : []}
      mensaje={mensaje}
      todoBien={todoBien}
      onVerificar={!cargando && !verificando && total > 0 ? onVerificar : undefined}
    />
  );
}

// ── Tarjeta de cada equipo ─────────────────────────────────────

/**
 * Un equipo como lista de verificación: si está en un vehículo y si tiene foto.
 * Clic abre el detalle con la evidencia; el menú abre la ficha del equipo o la edición.
 */
function ModemRevisionCard({ modem, onVer, onEditar, onVerEquipo, reporte, onVerReporte }) {
  const [anchor, setAnchor] = useState(null);
  const chips = getChips(modem);
  const subtitulo = [
    modem.modelo,
    chips.length ? `Chip ${chips.map(getChipNumber).join(", ")}` : null,
  ].filter(Boolean).join(" · ") || (modem.num_sim ? `SIM ${modem.num_sim}` : "");
  const tipo = tipoBandera(modem);

  return (
    <Card variant="outlined" sx={{ borderRadius: "10px" }}>
      <CardActionArea onClick={onVer} component="div">
        <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1.75 } }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ width: 44, height: 44, borderRadius: "8px", bgcolor: "rgba(2,132,199,0.14)", color: AZUL_EQUIPOS, display: "grid", placeItems: "center", flexShrink: 0 }}>
              <MemoryIcon />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                {modem.emai || modem.num_sim || "—"}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>{subtitulo}</Typography>
            </Box>
            <IconButton
              size="small"
              aria-label="Opciones"
              onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <MoreVertIcon />
            </IconButton>
          </Stack>

          <Stack spacing={0.75} sx={{ mt: 1.25 }}>
            {/* La placa es el título del grupo; aquí solo se avisa si falta. */}
            {!tienePlaca(modem) && <ItemVerificacion ok={false} textoFalta={SIN_VEHICULO} />}
            <ItemVerificacion
              ok={tieneFoto(modem)}
              textoOk="Tiene foto de la instalación"
              textoFalta="Sin foto de la instalación"
              advertencia
            />
            <LineaReporte reporte={reporte} onClick={onVerReporte} />
            {(tipo || modem.ip) && (
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                {tipo && <Chip size="small" variant="outlined" label={tipo} />}
                {modem.ip && <Chip size="small" variant="outlined" label={modem.ip} />}
              </Stack>
            )}
            {modem.observacion && <Observacion texto={modem.observacion} />}
          </Stack>
        </CardContent>
      </CardActionArea>

      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        <MenuItem onClick={() => { setAnchor(null); onVerEquipo(); }}>
          <ListItemIcon><MemoryIcon fontSize="small" /></ListItemIcon>
          Ver ficha del equipo
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); onEditar(); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          Editar datos y foto
        </MenuItem>
      </Menu>
    </Card>
  );
}
